import {
  ChoiceChip,
  Field,
  TextArea,
  TextInput,
} from '@/components/explore/CreatorFields'
import type { Audience, FormErrors, SetField, WizardForm } from './types'

const onlyDigits = (value: string) => value.replace(/\D/g, '')

/**
 * Passo 6 — para quem você trabalha e a sua história.
 *
 * É o passo que mais alimenta a IA: público, tempo de estrada e o que a pessoa
 * conta com as próprias palavras. Daí a descrição vir com uma pergunta em cima
 * em vez de um campo mudo — texto em branco é a resposta mais comum quando
 * ninguém diz o que escrever.
 *
 * Os placeholders NÃO trazem exemplo de valor ("Ex.: 5" nos anos de
 * experiência): número de exemplo num campo numérico é lido como sugestão do
 * que responder.
 */
export default function StepAudience({
  data,
  setData,
  audiences,
  errors,
}: {
  data: WizardForm
  setData: SetField
  audiences: Audience[]
  errors: FormErrors
}) {
  const allSelected = data.audiences.length === audiences.length

  const toggleAudience = (key: string) => {
    setData(
      'audiences',
      data.audiences.includes(key)
        ? data.audiences.filter((k) => k !== key)
        : [...data.audiences, key],
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-gray-800">
            Quem você atende? <span className="text-red-500">*</span>
          </p>
          <button
            type="button"
            onClick={() =>
              setData(
                'audiences',
                allSelected ? [] : audiences.map((a) => a.key),
              )
            }
            className="shrink-0 text-xs font-semibold text-primary hover:underline"
          >
            {allSelected ? 'Limpar seleção' : 'Marcar todos'}
          </button>
        </div>

        <div className="mt-2 flex flex-wrap gap-2">
          {audiences.map((audience) => (
            <ChoiceChip
              key={audience.key}
              label={audience.label}
              selected={data.audiences.includes(audience.key)}
              onClick={() => toggleAudience(audience.key)}
            />
          ))}
        </div>
        {errors.audiences && (
          <p className="mt-1.5 text-xs text-red-600">
            {Array.isArray(errors.audiences)
              ? errors.audiences[0]
              : errors.audiences}
          </p>
        )}
      </div>

      <Field
        label="Há quantos anos você faz isso? (opcional)"
        error={errors.years_experience}
      >
        <TextInput
          value={data.years_experience}
          onChange={(v) => setData('years_experience', onlyDigits(v))}
          placeholder="Digite o número de anos"
          inputMode="numeric"
          invalid={Boolean(errors.years_experience)}
        />
      </Field>

      <Field
        label="Conte um pouco sobre o seu negócio (opcional)"
        hint="Do seu jeito, sem se preocupar com o texto — quem escreve bonito é a IA, com o que você contar aqui."
      >
        <TextArea
          value={data.brief_description}
          onChange={(v) => setData('brief_description', v)}
          rows={5}
          placeholder="O que você faz, como começou, o que te diferencia"
        />
      </Field>

      <div>
        <p className="text-sm font-semibold text-gray-800">
          Quer complementar seu perfil? (opcional)
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Até 3 links extras. Seu site e Instagram já cadastrados fazem parte da
          nossa análise — não precisa repetir.
        </p>
        <div className="mt-2 space-y-2">
          {data.extra_links.map((link, index) => (
            <TextInput
              // 3 slots fixos por posição — a ordem É a identidade do campo.
              key={index}
              value={link}
              onChange={(v) => {
                const next = [...data.extra_links]
                next[index] = v
                setData('extra_links', next)
              }}
              placeholder="Cole um link"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
